const LATIN_SCRIPT_REGEX = /\p{Script=Latin}/u
const COMMON_SCRIPT_REGEX = /\p{Script=Common}/u
const ALLOWED_PUNCTUATION_REGEX = /[\s'-]/

const URL_LIKE_REGEX =
  /\b(?:www\.|https?:\/\/|\.(?:com|org|net|ru|vom|info|xyz))\b/i

const MAX_USER_INPUT_LENGTH = 100
const MIN_REPEATED_CHAR_SPAM_LENGTH = 10

const isAllowedInputCharacter = (char: string): boolean => {
  if (ALLOWED_PUNCTUATION_REGEX.test(char)) {
    return true
  }

  return LATIN_SCRIPT_REGEX.test(char) || COMMON_SCRIPT_REGEX.test(char)
}

/**
 * True when `value` contains characters outside Latin script, Common symbols,
 * whitespace, apostrophe, or hyphen. Use for form fields that expect German/Latin text.
 */
export const containsDisallowedInputCharacters = (value: string): boolean => {
  for (const char of value) {
    if (!isAllowedInputCharacter(char)) {
      return true
    }
  }

  return false
}

/**
 * True for bot/scraper-style text (disallowed characters, URL-like, spam patterns, etc.).
 * Empty or whitespace-only values are not invalid (required-field checks stay with the caller).
 */
export const isInvalidUserInput = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) {
    return false
  }

  if (trimmed.length > MAX_USER_INPUT_LENGTH) {
    return true
  }

  if (containsDisallowedInputCharacters(trimmed)) {
    return true
  }

  if (URL_LIKE_REGEX.test(trimmed)) {
    return true
  }

  const withoutWhitespace = trimmed.replace(/\s/g, '')
  if (
    withoutWhitespace.length >= MIN_REPEATED_CHAR_SPAM_LENGTH &&
    new Set(withoutWhitespace).size <= 2
  ) {
    return true
  }

  return false
}

/** True when `value` passes validation (not invalid / junk). */
export const isValidUserInput = (value: string): boolean =>
  !isInvalidUserInput(value)
